import React from 'react';
import { Job, JobStatus } from '../../job-summary/Interfaces';
import { FeedEmailData } from '../../mail/components/EmailFeedContent';
import { getJobBadgeColor, getJobButtonClass } from '../utils/statusHelpers';
import JobStatusDropdown from './JobStatusDropdown';
import JobEmailPreview from './JobEmailPreview';

export interface JobRowProps {
  job: Job;
  emails: FeedEmailData[];
  isJobActive: boolean;
  currentSessionMinutes: number;
  formatTime: (minutes: number) => string;
  formatDueDate: (dueDate: string | undefined) => string;
  statusDropdownOpen: boolean;
  onJobClick: (e: React.MouseEvent<HTMLButtonElement>, job: Job) => void;
  onDateClick: (e: React.MouseEvent<HTMLElement>, job: Job) => void;
  onStatusDropdownTriggerClick: (e: React.MouseEvent) => void;
  onStatusChange: (status: JobStatus) => void;
  onEmailClick: (email: FeedEmailData, e: React.MouseEvent) => void;
}

const JobRow: React.FC<JobRowProps> = ({
  job,
  emails,
  isJobActive,
  currentSessionMinutes,
  formatTime,
  formatDueDate,
  statusDropdownOpen,
  onJobClick,
  onDateClick,
  onStatusDropdownTriggerClick,
  onStatusChange,
  onEmailClick,
}) => {
  const totalSessionMinutes = job.workSessions?.reduce((total, session) => total + session.durationMinutes, 0) || 0;

  return (
    <div 
      className="border border-gray-200 rounded p-3"
      style={{ backgroundColor: 'rgba(0,0,0,0.01)' }}
    >
      {/* Job Header Row */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <button
          className={`btn btn-sm d-inline-flex align-items-center ${getJobButtonClass(job, isJobActive)}`}
          style={{ borderRadius: '0.475rem', minHeight: '32px' }}
          onClick={(e) => {
            e.stopPropagation();
            onJobClick(e, job);
          }}
        >
          {(isJobActive || job.status === 'Active') && (
            <div className="pulse-dot me-2"></div>
          )}
          <span className="fw-bold">
            {job.jobLabel || `Job #${job.id.slice(0, 8)}`}
          </span>
        </button>
        
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Time Logged */}
          {totalSessionMinutes > 0 && (
            <span 
              className="badge fs-8 fw-bold"
              style={{ backgroundColor: getJobBadgeColor(job, isJobActive), color: 'white' }}
              title="Total time logged"
            >
              ⏱️ {formatTime(totalSessionMinutes)}
            </span>
          )}
          
          {/* Due Date */}
          <span 
            className="badge fs-8 fw-bold cursor-pointer"
            style={{ backgroundColor: getJobBadgeColor(job, isJobActive), color: 'white' }}
            onClick={(e) => onDateClick(e, job)}
            title="Click to change due date"
          >
            Due: {formatDueDate(job.dueDate)}
          </span>
          
          {/* Status Dropdown */}
          <JobStatusDropdown
            job={job}
            isJobActive={isJobActive}
            currentSessionMinutes={currentSessionMinutes}
            formatTime={formatTime}
            show={statusDropdownOpen}
            onTriggerClick={onStatusDropdownTriggerClick}
            onStatusChange={onStatusChange}
          />
        </div>
      </div>
      
      {/* Recent Emails for this Job */}
      <JobEmailPreview
        emails={emails}
        jobId={job.id}
        onEmailClick={onEmailClick}
      />
    </div>
  );
};

export default JobRow;
